import { createRouter, createWebHistory } from 'vue-router'
import api from '../services/api'
import { msalInstance as msal } from '../plugins/msalConfig'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import(/* webpackChunkName: "Home" */ '../views/Home.vue')
  },
  {
    path: '/seller',
    name: 'SellerLayout',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(
        /* webpackChunkName: "sellerLayout" */ '../layouts/SellerLayout.vue'
      ),
    children: [
      {
        // vue-router 4: Default-/Index-Child = leerer Pfad statt '/' (in vue-router 3
        // wurde ein Child-`path: '/'` als Default-Child am Parent-Pfad gerendert; in
        // vue-router 4 wäre '/' ein absoluter Root-Pfad). Rendert unter '/seller'.
        path: '',
        name: 'verkauf',
        component: () =>
          import(/* webpackChunkName: "sellerPage" */ '../views/Seller.vue'),
        meta: {
          roles: ['isSellerUser']
        }
      },
      {
        path: '/buyer',
        name: 'einkauf',
        component: () =>
          import(/* webpackChunkName: "sellerPage" */ '../views/Buyer.vue'),
        meta: {
          roles: ['isPurchaserUser']
        }
      },
      {
        path: '/seller/models',
        name: 'sellerModels',
        component: () =>
          import(/* webpackChunkName: "sellerModel" */ '../views/Models.vue'),
        meta: {
          roles: ['isSellerUser']
        }
      },
      {
        path: '/buyer/models',
        name: 'buyerModels',
        component: () =>
          import(/* webpackChunkName: "sellerModel" */ '../views/Models.vue'),
        meta: {
          roles: ['isPurchaserUser']
        }
      },
      {
        path: '/buyer/files',
        name: 'buyerFiles',
        component: () =>
          import(/* webpackChunkName: "buyerFiles" */ '../views/Files.vue'),
        meta: {
          roles: ['isPurchaserUser']
        }
      }
    ]
  },
  {
    path: '/search',
    name: 'searchPage',
    component: () =>
      import(/* webpackChunkName: "searchPage" */ '../views/SearchPage.vue')
  },
  {
    path: '/news',
    name: 'fullLayout',
    meta: { roles: ['isNewsUser'] },
    component: () =>
      import(
        /* webpackChunkName: "systemLayout" */ '../layouts/FullLayout.vue'
      ),
    children: [
      {
        path: '/access-denied',
        name: 'denied',
        component: () =>
          import(/* webpackChunkName: "system" */ '../views/AccessDenied.vue'),
        meta: { roles: ['all'] }
      },
      {
        // vue-router 4: Default-Child von '/news' -> leerer Pfad (rendert News unter '/news').
        path: '',
        name: 'news',
        component: () =>
          import(/* webpackChunkName: "system" */ '../views/News.vue'),
        meta: { roles: ['isNewsUser'] }
      },
      {
        path: '/system/:supplierId?/:modelId?/:pathEinkauf?',
        name: 'system',
        component: () =>
          import(/* webpackChunkName: "system" */ '../views/System.vue'),
        meta: {
          roles: ['isAppAdminUser', 'isUserManagementUser', 'isCalculationUser']
        }
      },
      {
        path: '/pdf-view',
        name: 'pdfView',
        component: () =>
          import(/* webpackChunkName: "system" */ '../views/PdfView.vue'),
        meta: {
          roles: ['seller', 'isUserManagementUser', 'isCalculationUser']
        }
      },
      {
        path: '/rabatte',
        name: 'rabatte',
        component: () =>
          import(/* webpackChunkName: "system" */ '../views/Rabatte.vue'),
        meta: { roles: ['seller', 'firmenAdmin', 'vmeAdmin'] }
      }
    ]
  },
  {
    path: '/seller/catalog-detail',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(
        /* webpackChunkName: "sellerLayout" */ '../layouts/CatalogDetailLayout.vue'
      ),
    children: [
      {
        // vue-router 4: Default-Child von '/seller/catalog-detail' -> leerer Pfad.
        // Der Name gehoert ans Kind, nicht an den Parent: liegt er am Parent,
        // rendert eine Navigation ueber den Namen das leere-Pfad-Kind nicht
        // (Router-4-Warnung "child without a name and an empty path").
        path: '',
        name: 'CatalogDetailModal',
        component: () =>
          import(
            /* webpackChunkName: "sellerPage" */ '../views/ModelDetail.vue'
          ),
        meta: { roles: ['isSellerUser'] }
      },
      {
        path: '/buyer/model/:id',
        name: 'buyerCatalogDetail',
        component: () =>
          import(
            /* webpackChunkName: "sellerModel" */ '../views/ModelDetail.vue'
          ),
        meta: { roles: ['isPurchaserUser'] }
      },
      {
        path: '/seller/model/:id',
        name: 'sellerCatalogDetail',
        component: () =>
          import(
            /* webpackChunkName: "sellerModel" */ '../views/ModelDetail.vue'
          ),
        meta: { roles: ['isSellerUser'] }
      }
    ]
  }
]

const router = createRouter({
  // vue-router 4: `mode: 'history'` + `base` -> createWebHistory(base).
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  const companyId = sessionStorage.getItem('companyId')
  // Ohne Sitzung schickt der axios-Interceptor keinen Authorization-Header — der
  // Aufruf endete dann zwangsläufig in einem 401, den der Response-Interceptor
  // als globalen Fehler anzeigte ("Ups, da ist etwas schiefgelaufen"), obwohl
  // lediglich der Login noch aussteht. App.vue holt die companyId nach der
  // Anmeldung ohnehin selbst; dieser Guard deckt nur spätere Navigationen ab.
  if (!companyId && msal.isAuthenticated) {
    try {
      const response = await api.getCurrentMerchant()
      if (response.data && response.data.data && response.data.data.merchantId) {
        sessionStorage.setItem('companyId', response.data.data.merchantId)
        console.log('router.beforeEach Company ID set:', response.data.data.merchantId)
      }
    } catch (e) {
      console.error('Error fetching current merchant in router:', e)
    }
  }
  next()
})

// Nach einem Deployment gibt es die Chunks des alten Stands nicht mehr. Ein Tab,
// der noch mit der alten Version laeuft, scheitert dann beim Nachladen einer Route
// und bleibt ohne Inhalt stehen. Wir laden die Zielseite einmal komplett neu; die
// Sperre verhindert eine Schleife, falls der Chunk tatsaechlich fehlt. Derselbe
// Schluessel wird von nginx (@stale_bundle in deployment/nginx/default.conf) genutzt.
const STALE_BUNDLE_RELOAD_KEY = 'ktw-stale-bundle-reload'

router.onError((error, to) => {
  const message = (error && error.message) || ''
  const chunkLoadFailed =
    (error && error.name === 'ChunkLoadError') ||
    /Loading (CSS )?chunk .+ failed/i.test(message)
  if (!chunkLoadFailed) {
    return
  }
  try {
    const now = Date.now()
    const last = Number(sessionStorage.getItem(STALE_BUNDLE_RELOAD_KEY)) || 0
    if (now - last < 10000) {
      return
    }
    sessionStorage.setItem(STALE_BUNDLE_RELOAD_KEY, String(now))
  } catch (e) {
    return
  }
  window.location.assign(to && to.fullPath ? to.fullPath : window.location.href)
})

export default router
